import { NgTemplateOutlet } from "@angular/common";
import { ChangeDetectionStrategy, Component, OnInit, ViewEncapsulation, inject, signal, computed } from "@angular/core";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { WorkflowConst } from "../workflow/workflow.const";
import { SeInit, SeInitService } from "./se.init.service";
import { S25ButtonComponent } from "../../standalone/s25.button.component";
import { S25SecurityLinkModule } from "../s25-security-link/s25.security.link.module";
import { Item } from "../../pojo/Item";
import { UserStore } from "../../stores/user.store";
import { CONNECT25_WORKFLOW_CREATE_ORG_DEFAULT_OLS_OBJECT_ID } from "./se.connect25.constants";
import { S25Util } from "../../util/s25-util";

@TypeManagerDecorator("s25-ng-se-init")
@Component({
    selector: "s25-ng-se-init",
    template: `
        @if (init()) {
            <div>
                <p class="c-margin-bottom--single">
                    Click Initialize below to create a Workflow cabinet and folder structure, and Connect25 master
                    definitions (categories, custom attributes, and engagement badges). This operation is safe to run
                    multiple times. After initializing, visit the Groups Tool to set up default OLS for Workflow
                    assignments.
                </p>
                @if (alreadyInitialized()) {
                    <div class="c-margin-bottom--single">
                        <p>System has already been initialized.</p>
                        <div class="c-margin-top--single">
                            <p class="c-margin-bottom--half">Configure security for Workflow components:</p>
                            <div class="c-margin-bottom--half">
                                <s25-security-link
                                    [itemTypeId]="EVENT_TYPE_ID"
                                    [itemId]="WORKFLOW_CABINET_ID"
                                    [nodeType]="'C'"
                                >
                                </s25-security-link>
                                <span class="c-margin-left--half">Workflow Cabinet</span>
                            </div>
                            <div class="c-margin-bottom--half">
                                <s25-security-link
                                    [itemTypeId]="EVENT_TYPE_ID"
                                    [itemId]="WORKFLOW_FOLDER_ID"
                                    [nodeType]="'F'"
                                >
                                </s25-security-link>
                                <span class="c-margin-left--half">Workflow Folder</span>
                            </div>
                            <p class="c-margin-bottom--half">
                                Default object security for organizations created by the Create Organization workflow
                                action (when configured here; otherwise general organization default security applies):
                            </p>
                            <div>
                                <s25-security-link
                                    [itemTypeId]="ORGANIZATION_TYPE_ID"
                                    [itemId]="c25WorkflowCreateOrgDefaultOlsObjectId"
                                >
                                </s25-security-link>
                                <span class="c-margin-left--half">Workflow Create Organization default security</span>
                            </div>
                        </div>
                    </div>
                } @else {
                    @if (status()) {
                        <div class="c-margin-bottom--single">
                            <p class="c-margin-bottom--half"><strong>Missing master definitions:</strong></p>
                            <ng-container
                                *ngTemplateOutlet="
                                    catalogSection;
                                    context: {
                                        title: 'Event Categories',
                                        items: status()?.catalog?.eventCategories ?? [],
                                        missingOnly: true,
                                    }
                                "
                            />
                            <ng-container
                                *ngTemplateOutlet="
                                    catalogSection;
                                    context: {
                                        title: 'Organization Categories',
                                        items: status()?.catalog?.organizationCategories ?? [],
                                        missingOnly: true,
                                    }
                                "
                            />
                            <ng-container
                                *ngTemplateOutlet="
                                    catalogSection;
                                    context: {
                                        title: 'Organization Custom Attributes',
                                        items: status()?.catalog?.organizationCustomAttributes ?? [],
                                        missingOnly: true,
                                    }
                                "
                            />
                            <ng-container
                                *ngTemplateOutlet="
                                    catalogSection;
                                    context: {
                                        title: 'Contact Custom Attributes',
                                        items: status()?.catalog?.contactCustomAttributes ?? [],
                                        missingOnly: true,
                                    }
                                "
                            />
                            <ng-container
                                *ngTemplateOutlet="
                                    catalogSection;
                                    context: {
                                        title: 'Engagement Badges',
                                        items: status()?.catalog?.engagementBadges ?? [],
                                        missingOnly: true,
                                    }
                                "
                            />
                        </div>
                    }
                }
                @if (lastResponse()) {
                    <div class="c-margin-bottom--single">
                        @if (hasErrors()) {
                            <h3 class="c-margin-bottom--half ngRed">Initialization completed with errors</h3>
                            <p class="c-margin-bottom--half ngRed">
                                Some steps did not finish successfully. Review the messages below, fix the underlying
                                issue if needed, then run Initialize again.
                            </p>
                        } @else {
                            <h3 class="c-margin-bottom--half ngGreen">Initialization Complete</h3>
                        }
                        @if (hasInsertedItems()) {
                            <div class="c-margin-bottom--single">
                                <h4 class="c-margin-bottom--half">Inserted:</h4>
                                <ng-container
                                    *ngTemplateOutlet="
                                        catalogSection;
                                        context: {
                                            title: 'Event Categories',
                                            items: lastResponse()?.catalog?.eventCategories ?? [],
                                            outcome: 'INSERTED',
                                        }
                                    "
                                />
                                <ng-container
                                    *ngTemplateOutlet="
                                        catalogSection;
                                        context: {
                                            title: 'Organization Categories',
                                            items: lastResponse()?.catalog?.organizationCategories ?? [],
                                            outcome: 'INSERTED',
                                        }
                                    "
                                />
                                <ng-container
                                    *ngTemplateOutlet="
                                        catalogSection;
                                        context: {
                                            title: 'Organization Custom Attributes',
                                            items: lastResponse()?.catalog?.organizationCustomAttributes ?? [],
                                            outcome: 'INSERTED',
                                        }
                                    "
                                />
                                <ng-container
                                    *ngTemplateOutlet="
                                        catalogSection;
                                        context: {
                                            title: 'Contact Custom Attributes',
                                            items: lastResponse()?.catalog?.contactCustomAttributes ?? [],
                                            outcome: 'INSERTED',
                                        }
                                    "
                                />
                                <ng-container
                                    *ngTemplateOutlet="
                                        catalogSection;
                                        context: {
                                            title: 'Engagement Badges',
                                            items: lastResponse()?.catalog?.engagementBadges ?? [],
                                            outcome: 'INSERTED',
                                        }
                                    "
                                />
                            </div>
                        }
                        @if (hasExistedItems()) {
                            <div class="c-margin-bottom--single">
                                <h4 class="c-margin-bottom--half">Already Existed:</h4>
                                <ng-container
                                    *ngTemplateOutlet="
                                        catalogSection;
                                        context: {
                                            title: 'Event Categories',
                                            items: lastResponse()?.catalog?.eventCategories ?? [],
                                            outcome: 'ALREADY_EXISTED',
                                        }
                                    "
                                />
                                <ng-container
                                    *ngTemplateOutlet="
                                        catalogSection;
                                        context: {
                                            title: 'Organization Categories',
                                            items: lastResponse()?.catalog?.organizationCategories ?? [],
                                            outcome: 'ALREADY_EXISTED',
                                        }
                                    "
                                />
                                <ng-container
                                    *ngTemplateOutlet="
                                        catalogSection;
                                        context: {
                                            title: 'Organization Custom Attributes',
                                            items: lastResponse()?.catalog?.organizationCustomAttributes ?? [],
                                            outcome: 'ALREADY_EXISTED',
                                        }
                                    "
                                />
                                <ng-container
                                    *ngTemplateOutlet="
                                        catalogSection;
                                        context: {
                                            title: 'Contact Custom Attributes',
                                            items: lastResponse()?.catalog?.contactCustomAttributes ?? [],
                                            outcome: 'ALREADY_EXISTED',
                                        }
                                    "
                                />
                                <ng-container
                                    *ngTemplateOutlet="
                                        catalogSection;
                                        context: {
                                            title: 'Engagement Badges',
                                            items: lastResponse()?.catalog?.engagementBadges ?? [],
                                            outcome: 'ALREADY_EXISTED',
                                        }
                                    "
                                />
                            </div>
                        }
                        @if (hasErrors()) {
                            <div class="c-margin-bottom--single">
                                <h4 class="c-margin-bottom--half ngRed">Details:</h4>
                                <ul>
                                    @for (error of lastResponse()!.errors; track error) {
                                        <li class="ngRed">{{ error }}</li>
                                    }
                                </ul>
                            </div>
                        }
                    </div>
                }
                @if (error()) {
                    <div class="c-margin-bottom--single">
                        <p class="ngRed">{{ error() }}</p>
                    </div>
                }
                <s25-ng-button [type]="'primary'" [onClick]="initialize" [disabled]="loading()">
                    {{ buttonText() }}
                </s25-ng-button>

                @if (userStore.isService25()) {
                    <div class="c-margin-top--double">
                        <h3 class="c-margin-bottom--half">Seed Data (optional)</h3>
                        <p class="c-margin-bottom--single">
                            Load sample Connect25 forms and workflows tagged with [Seed]. Safe to run multiple times;
                            existing demo objects are skipped.
                        </p>
                        @if (demoLoaded()) {
                            <p class="c-margin-bottom--single">Seed data is already loaded on this instance.</p>
                        }
                        @if (demoLastResponse()) {
                            <div class="c-margin-bottom--single">
                                @if (hasDemoInsertedItems()) {
                                    <div class="c-margin-bottom--half">
                                        <strong>Inserted:</strong>
                                        @if (demoLastResponse()!.inserted.ingestionPoints.length > 0) {
                                            <div>
                                                Forms: {{ demoLastResponse()!.inserted.ingestionPoints.join(", ") }}
                                            </div>
                                        }
                                        @if (demoLastResponse()!.inserted.workflowProcesses.length > 0) {
                                            <div>
                                                Workflows:
                                                {{ demoLastResponse()!.inserted.workflowProcesses.join(", ") }}
                                            </div>
                                        }
                                    </div>
                                }
                                @if (hasDemoExistedItems()) {
                                    <div class="c-margin-bottom--half">
                                        <strong>Already existed:</strong>
                                        @if (demoLastResponse()!.alreadyExisted.ingestionPoints.length > 0) {
                                            <div>
                                                Forms:
                                                {{ demoLastResponse()!.alreadyExisted.ingestionPoints.join(", ") }}
                                            </div>
                                        }
                                        @if (demoLastResponse()!.alreadyExisted.workflowProcesses.length > 0) {
                                            <div>
                                                Workflows:
                                                {{ demoLastResponse()!.alreadyExisted.workflowProcesses.join(", ") }}
                                            </div>
                                        }
                                    </div>
                                }
                                @if (hasDemoErrors()) {
                                    <div>
                                        <strong class="ngRed">Errors:</strong>
                                        <ul>
                                            @for (demoError of demoLastResponse()!.errors; track demoError) {
                                                <li class="ngRed">{{ demoError }}</li>
                                            }
                                        </ul>
                                    </div>
                                }
                            </div>
                        }
                        @if (demoError()) {
                            <p class="ngRed c-margin-bottom--single">{{ demoError() }}</p>
                        }
                        <s25-ng-button
                            [type]="'primary'"
                            [onClick]="seedDemoData"
                            [disabled]="demoLoading() || demoLoaded()"
                        >
                            {{ demoButtonText() }}
                        </s25-ng-button>
                    </div>
                }
            </div>
        }

        <ng-template
            #catalogSection
            let-title="title"
            let-items="items"
            let-missingOnly="missingOnly"
            let-outcome="outcome"
        >
            @if (visibleCatalogItems(items, missingOnly, outcome).length > 0) {
                <div class="c-margin-bottom--half">
                    <strong>{{ title }}:</strong>
                    <ul>
                        @for (item of visibleCatalogItems(items, missingOnly, outcome); track catalogTrack(item)) {
                            <li>{{ formatCatalogItem(item) }}</li>
                        }
                    </ul>
                </div>
            }
        </ng-template>
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [NgTemplateOutlet, S25ButtonComponent, S25SecurityLinkModule],
})
export class SeInitComponent implements OnInit {
    readonly userStore = inject(UserStore);

    readonly init = signal(false);
    readonly loading = signal(false);
    readonly alreadyInitialized = signal(false);
    readonly status = signal<SeInit.InitStatusResponse | null>(null);
    readonly lastResponse = signal<SeInit.InitResponse | null>(null);
    readonly error = signal<string | null>(null);
    readonly demoLoaded = signal(false);
    readonly demoLoading = signal(false);
    readonly demoLastResponse = signal<SeInit.DemoSeedResponse | null>(null);
    readonly demoError = signal<string | null>(null);

    readonly EVENT_TYPE_ID = Item.Ids.Event;
    readonly ORGANIZATION_TYPE_ID = Item.Ids.Organization;
    readonly WORKFLOW_CABINET_ID = WorkflowConst.WorkflowCabinetId;
    readonly WORKFLOW_FOLDER_ID = WorkflowConst.WorkflowFolderId;
    readonly c25WorkflowCreateOrgDefaultOlsObjectId = CONNECT25_WORKFLOW_CREATE_ORG_DEFAULT_OLS_OBJECT_ID;

    readonly buttonText = computed(() => (this.loading() ? "Initializing..." : "Initialize"));

    readonly demoButtonText = computed(() => (this.demoLoading() ? "Creating seed data..." : "Create Seed Data"));

    readonly hasDemoInsertedItems = computed(() => {
        const response = this.demoLastResponse();
        return (
            response && (response.inserted.ingestionPoints.length > 0 || response.inserted.workflowProcesses.length > 0)
        );
    });

    readonly hasDemoExistedItems = computed(() => {
        const response = this.demoLastResponse();
        return (
            response &&
            (response.alreadyExisted.ingestionPoints.length > 0 || response.alreadyExisted.workflowProcesses.length > 0)
        );
    });

    readonly hasDemoErrors = computed(() => {
        const response = this.demoLastResponse();
        return response && response.errors.length > 0;
    });

    readonly hasInsertedItems = computed(() => {
        const response = this.lastResponse();
        if (!response) {
            return false;
        }
        return (
            response.inserted.categories.length > 0 ||
            response.inserted.customAttributes.length > 0 ||
            response.inserted.engagementBadges.length > 0
        );
    });

    readonly hasExistedItems = computed(() => {
        const response = this.lastResponse();
        if (!response) {
            return false;
        }
        return (
            response.alreadyExisted.categories.length > 0 ||
            response.alreadyExisted.customAttributes.length > 0 ||
            response.alreadyExisted.engagementBadges.length > 0
        );
    });

    readonly hasErrors = computed(() => {
        const response = this.lastResponse();
        return response != null && response.errors.length > 0;
    });

    initialize = async () => {
        this.loading.set(true);
        this.error.set(null);
        try {
            const response = await SeInitService.initialize();
            this.lastResponse.set(response);
            await this.checkStatus();
        } catch (caughtError) {
            console.error("Error initializing system:", caughtError);
            const errorBody =
                (caughtError as { data?: SeInit.InitResponse; error?: SeInit.InitResponse })?.data ??
                (caughtError as { error?: SeInit.InitResponse })?.error;
            if (errorBody?.errors?.length) {
                this.lastResponse.set(errorBody);
                this.error.set(
                    "Connect25 initialization could not be completed. Review the details below and try again.",
                );
            } else {
                this.error.set(
                    `Connect25 initialization could not be completed. ${S25Util.errorText(
                        caughtError,
                        "Check that the server is available and try again.",
                    )}`,
                );
            }
        } finally {
            this.loading.set(false);
        }
    };

    checkStatus = async () => {
        const statusResponse = await SeInitService.getStatus();
        this.status.set(statusResponse);
        this.alreadyInitialized.set(statusResponse.isInitialized);
    };

    visibleCatalogItems(
        items: SeInit.CatalogItem[] | undefined,
        missingOnly?: boolean,
        outcome?: SeInit.CatalogOutcome,
    ): SeInit.CatalogItem[] {
        if (!items?.length) {
            return [];
        }
        if (missingOnly) {
            return items.filter((item) => item.outcome === "MISSING");
        }
        if (outcome) {
            return items.filter((item) => item.outcome === outcome);
        }
        return items;
    }

    formatCatalogItem(item: SeInit.CatalogItem): string {
        const idPart = item.id != null ? `ID: ${item.id}` : item.badgeKey ? `key: ${item.badgeKey}` : "";
        const typePart = item.dataType ? `, ${item.dataType}` : "";
        if (idPart) {
            return `${item.catalogLabel} (${idPart}${typePart})`;
        }
        return item.catalogLabel;
    }

    catalogTrack(item: SeInit.CatalogItem): string {
        return item.id != null ? String(item.id) : (item.badgeKey ?? item.catalogLabel);
    }

    checkDemoStatus = async () => {
        const demoStatus = await SeInitService.getDemoSeedStatus();
        this.demoLoaded.set(demoStatus.demoLoaded);
    };

    seedDemoData = async () => {
        this.demoLoading.set(true);
        this.demoError.set(null);
        try {
            const response = await SeInitService.seedDemoData();
            this.demoLastResponse.set(response);
            await this.checkDemoStatus();
        } catch (caughtError) {
            console.error("Error seeding seed data:", caughtError);
            const errorBody =
                (caughtError as { data?: SeInit.DemoSeedResponse; error?: SeInit.DemoSeedResponse })?.data ??
                (caughtError as { error?: SeInit.DemoSeedResponse })?.error;
            if (errorBody?.errors?.length) {
                this.demoLastResponse.set(errorBody);
                this.demoError.set(null);
            } else {
                this.demoError.set(
                    "Failed to create seed data: " +
                        (caughtError instanceof Error ? caughtError.message : String(caughtError)),
                );
            }
        } finally {
            this.demoLoading.set(false);
        }
    };

    async ngOnInit() {
        await this.checkStatus();
        if (this.userStore.isService25()) {
            await this.checkDemoStatus();
        }
        this.init.set(true);
    }
}
